import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@/components/UI/Button';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import { Control, FormProvider, useForm, useFormState } from 'react-hook-form-latest';
import Header from '@/components/UI/Header';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { useRequest } from '@/support/Hooks/request';
import { getProviderComponent, providerNameMap, ssoProviders } from './SingleSignOn.utils';
import { errorToast, successToast } from '@/helpers';
import type { SsoProvider } from './SingleSignOn.types';
import { RouteService } from '@/support/RouteService';
import SoftTestSsoLink from './SoftTestSsoLink';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

export interface FormValues {
  azure: {
    enabled: boolean;
    tenant: string;
    client_id: string;
    client_secret: string;
  };
  okta: {
    enabled: boolean;
    domain: string;
    client_id: string;
    client_secret: string;
  };
  saml2: {
    enabled: boolean;
    audience: string;
    issuer: string;
  };
}

const SingleSignOn = () => {
  const providerState = useState(0);
  const [provider, setProvider] = providerState;

  const { availableProviders, activeProvider } = useMemo(() => {
    return {
      availableProviders: ssoProviders,
      activeProvider: ssoProviders[provider],
    };
  }, [provider]);

  useEffect(() => {
    if (provider >= availableProviders.length) {
      setProvider(0);
    }
  }, [availableProviders.length, provider, setProvider]);

  const [hasSaml2MetadataFile, setHasSaml2MetadataFile] = useState(false);

  const [providerLoading, setProviderLoading] = useState<Record<SsoProvider, boolean>>({
    azure: true,
    okta: true,
    saml2: true,
  });

  const [savedEnabled, setSavedEnabled] = useState<Record<SsoProvider, boolean>>({
    azure: false,
    okta: false,
    saml2: false,
  });

  const handleProviderLoadingChange = useCallback((provider: SsoProvider, loading: boolean) => {
    setProviderLoading((prev) => ({ ...prev, [provider]: loading }));
  }, []);

  const handleSavedEnabledChange = useCallback((provider: SsoProvider, enabled: boolean) => {
    setSavedEnabled((prev) => ({ ...prev, [provider]: enabled }));
  }, []);

  const handleSaml2MetadataFileChange = useCallback((hasFile: boolean) => {
    setHasSaml2MetadataFile(hasFile);
  }, []);

  // Memoize provider props to prevent unnecessary re-renders
  const providerProps = useMemo(
    () => ({
      azure: {
        onLoadingChange: (loading: boolean) => handleProviderLoadingChange('azure', loading),
        onSavedEnabledChange: (enabled: boolean) => handleSavedEnabledChange('azure', enabled),
      },
      okta: {
        onLoadingChange: (loading: boolean) => handleProviderLoadingChange('okta', loading),
        onSavedEnabledChange: (enabled: boolean) => handleSavedEnabledChange('okta', enabled),
      },
      saml2: {
        onMetadataFileChange: handleSaml2MetadataFileChange,
        onLoadingChange: (loading: boolean) => handleProviderLoadingChange('saml2', loading),
        onSavedEnabledChange: (enabled: boolean) => handleSavedEnabledChange('saml2', enabled),
      },
    }),
    [handleProviderLoadingChange, handleSavedEnabledChange, handleSaml2MetadataFileChange]
  );

  const useFormMethods = useForm<FormValues>({
    defaultValues: {
      azure: { enabled: false, tenant: '', client_id: '', client_secret: '' },
      okta: { enabled: false, domain: '', client_id: '', client_secret: '' },
      saml2: { enabled: false, audience: '', issuer: '' },
    },
  });
  const { handleSubmit, control } = useFormMethods;

  const {
    sendRequest: updateSSOProvider,
    requestSending: updatingSSOProvider,
    requestError,
  } = useRequest({
    route: 'team.sso_providers',
    params: { type: activeProvider },
    method: 'put',
  });

  const onSubmit = async (data: Partial<Record<SsoProvider, any>>) => {
    const providerFormData = data[activeProvider] ?? {};
    const providerName = providerNameMap[activeProvider];
    try {
      await updateSSOProvider(providerFormData);
      handleSavedEnabledChange(activeProvider, Boolean(providerFormData.enabled));
      useFormMethods.reset(useFormMethods.getValues());
      successToast(`Updated SSO settings for ${providerName}`);
    } catch (error) {
      ExceptionHandler.captureException(
        new InVivoError(`Failed to update SSO settings for ${providerName}`, { cause: error }),
        { activeProvider, context: 'SingleSignOn.onSubmit' }
      );
      errorToast(`Could not update SSO settings for ${providerName}`);
    }
  };

  return (
    <>
      <div>
        <Header mainHeaderText="Setup single sign on (SSO)" />
        <p className="lh-copy f6">
          Enter your selected provider details to enable SSO.{' '}
          <a href={RouteService.help('single_sign_on')} target="_blank" rel="noopener noreferrer" className="link blue">
            How do I setup my team SSO?
          </a>
        </p>
      </div>
      <div className="ui-card mw7 mv4">
        {requestError && <ApiErrorBanner className="mb4" error={requestError} />}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Tabs outerState={providerState}>
            <div className="ph3 pt3 mb3 bb b--moon-gray">
              {availableProviders.map((provider, index) => (
                <Tab key={index} className="f5">
                  {providerNameMap[provider]}
                </Tab>
              ))}
            </div>
            {availableProviders.map((provider, index) => (
              <Panel key={index}>
                <FormProvider {...useFormMethods}>
                  {getProviderComponent(provider, providerProps[provider])}
                </FormProvider>
              </Panel>
            ))}
          </Tabs>
          <SsoFormFooter
            control={control}
            activeProvider={activeProvider}
            savedEnabled={savedEnabled[activeProvider]}
            providerLoading={providerLoading[activeProvider]}
            updatingSSOProvider={updatingSSOProvider}
            saveDisabledExtra={activeProvider === 'saml2' && !hasSaml2MetadataFile}
          />
        </form>
      </div>
    </>
  );
};

interface SsoFormFooterProps {
  control: Control<FormValues>;
  activeProvider: SsoProvider;
  savedEnabled: boolean;
  providerLoading: boolean;
  updatingSSOProvider: boolean;
  saveDisabledExtra: boolean;
}

/**
 * Footer row with Save + Soft-test link. Isolates the form-dirty subscription
 * here so the parent form does not re-render on every field change — only this
 * small component does.
 */
const SsoFormFooter: FC<SsoFormFooterProps> = ({
  control,
  activeProvider,
  savedEnabled,
  providerLoading,
  updatingSSOProvider,
  saveDisabledExtra,
}) => {
  const { dirtyFields } = useFormState({ control, name: activeProvider });
  const activeProviderDirty = Boolean(dirtyFields[activeProvider]);
  const softTestDisabled = !savedEnabled || activeProviderDirty || providerLoading || updatingSSOProvider;
  const saveDisabled = updatingSSOProvider || providerLoading || saveDisabledExtra;

  return (
    <div className="pa3 flex items-center">
      <Button submit disabled={saveDisabled}>
        Save
      </Button>
      <span className="ml3">
        <SoftTestSsoLink provider={activeProvider} disabled={softTestDisabled} />
      </span>
    </div>
  );
};

export default SingleSignOn;
